/**
 * AiPreviewItemCard — карточка одного AI-сгенерированного шага наряда.
 *
 * P1 Enterprise Hardening:
 *  - бейдж физической валидации (physical_status от AIItemValidator)
 *  - inline dropdown стойки / каталога при неточном совпадении
 *  - dropdown порта из candidate_ports для PATCH_CABLE
 *
 * Используется в AiWorkOrderModal и JiraTaskSelector (общий компонент вместо дублирования).
 */
import { AlertTriangle, CheckCircle2, ShieldAlert, HelpCircle } from 'lucide-react'
import type { WorkOrderItemPreview, CandidatePort } from '../../types/workOrder'
import { ITEM_TYPE_ICONS } from '../../types/workOrder'
import { formatPatchCableInfo } from './patchCableTooltip.logic'
import { isPhysicallyBlocked, applyResolution, uHeightOrDefault } from './aiPreview.logic'

export interface RackOption {
  id: number
  label: string
}

export interface CatalogOption {
  id: number
  label: string
  u_height?: number
}

interface AiPreviewItemCardProps {
  item: WorkOrderItemPreview
  index: number
  racks: RackOption[]
  catalog: CatalogOption[]
  onChange: (index: number, updated: WorkOrderItemPreview) => void
}

const selectCls =
  'w-full text-xs px-2 py-1.5 rounded-lg border border-border/60 bg-bg-input text-text-primary ' +
  'focus:border-accent focus:ring-1 focus:ring-accent/30 outline-none'

function PhysicalBadge({ item }: { item: WorkOrderItemPreview }) {
  const status = item.metadata?.physical_status as string | undefined
  if (!status) return null
  const reason = item.metadata?.physical_reason as string | undefined

  if (status === 'OK') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-status-normal font-semibold" title={reason}>
        <CheckCircle2 className="w-3 h-3" /> физически валидно
      </span>
    )
  }
  if (isPhysicallyBlocked(item)) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-status-critical font-semibold" data-testid="physical-blocked">
        <ShieldAlert className="w-3 h-3" /> {reason || status}
      </span>
    )
  }
  // UNVERIFIED
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-text-muted" title={reason}>
      <HelpCircle className="w-3 h-3" /> {reason || 'не проверено'}
    </span>
  )
}

export function AiPreviewItemCard({ item, index, racks, catalog, onChange }: AiPreviewItemCardProps) {
  const meta = (item.metadata ?? {}) as Record<string, unknown>
  const blocked = isPhysicallyBlocked(item)
  const pc = item.item_type === 'PATCH_CABLE' ? formatPatchCableInfo(item.metadata) : null

  const isInstall = item.item_type === 'INSTALL_EQUIPMENT'
  const needsRack =
    (isInstall || item.item_type === 'REMOVE_EQUIPMENT') &&
    (!meta.rack_id || meta.rack_match_confidence !== 'exact')
  const needsCatalog = isInstall && !!meta.catalog_match_confidence && meta.catalog_match_confidence !== 'exact'
  const candidatePorts = (meta.candidate_ports as CandidatePort[] | undefined) ?? []
  const needsPort = item.item_type === 'PATCH_CABLE' && candidatePorts.length > 0
  const uHeight = uHeightOrDefault(meta.u_height)
  const freeIntervals = ((meta.free_intervals as { start: number; end: number; slots: number }[] | undefined) ?? [])
    .filter(iv => iv.slots >= uHeight)
  const showUPicker = isInstall && !!meta.rack_id
  const currentU = (meta.position_u ?? meta.u_position) as number | undefined

  const patch = (p: Record<string, unknown>) => onChange(index, applyResolution(item, p))

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border text-sm ${
        blocked
          ? 'border-status-critical/40 bg-status-critical/5'
          : item.has_unresolved
            ? 'border-status-warning/30 bg-status-warning/5'
            : 'border-border/40 bg-bg-panel/40'
      }`}
      data-testid={`ai-preview-item-${index}`}
    >
      <span className="text-base shrink-0">{ITEM_TYPE_ICONS[item.item_type] || '📝'}</span>
      <div className="flex-1 min-w-0">
        <p className="text-text-primary">{item.description}</p>

        {/* P4-2: объяснение выбора (от LLM или deterministic fallback валидатора) */}
        {typeof meta.reasoning === 'string' && meta.reasoning && (
          <p className="mt-0.5 text-[11px] italic text-text-muted" data-testid={`reasoning-${index}`}>
            💡 {meta.reasoning}
          </p>
        )}

        {pc && (
          <div className="mt-1.5 font-mono text-[10px] bg-bg-input/60 border border-border/30 rounded px-2 py-1.5 space-y-0.5">
            <div>
              <span className="text-text-muted">От:</span>{' '}
              <span className={pc.fromPortMissing ? 'text-status-warning' : 'text-text-secondary'}>{pc.from}</span>
            </div>
            <div>
              <span className="text-text-muted">До:</span>{' '}
              <span className={pc.toPortMissing ? 'text-status-warning' : 'text-text-secondary'}>{pc.to}</span>
            </div>
            <div>
              <span className="text-text-muted">Кабель:</span>{' '}
              <span className="text-text-secondary">{pc.cable}</span>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-1">
          <span className="font-mono text-[10px] text-accent px-1.5 py-0.5 bg-accent/10 rounded inline-block">
            {item.item_type}
          </span>
          <PhysicalBadge item={item} />
          {item.has_unresolved && !blocked && (
            <span className="text-[10px] text-status-warning font-semibold">⚠ требует уточнения</span>
          )}
        </div>

        {/* Inline resolve dropdowns (P1-4) */}
        {(needsRack || needsCatalog || needsPort || showUPicker) && (
          <div className="mt-2 grid gap-1.5">
            {needsRack && (
              <label className="block">
                <span className="text-[10px] text-text-muted">Стойка:</span>
                <select
                  className={selectCls}
                  value={(meta.rack_id as number | undefined) ?? ''}
                  onChange={e =>
                    patch({ rack_id: e.target.value ? Number(e.target.value) : null, rack_match_confidence: 'exact' })
                  }
                  data-testid={`resolve-rack-${index}`}
                >
                  <option value="">— выберите стойку —</option>
                  {racks.map(r => (
                    <option key={r.id} value={r.id}>{r.label}</option>
                  ))}
                </select>
              </label>
            )}
            {showUPicker && (
              <label className="block">
                <span className="text-[10px] text-text-muted">Свободный U:</span>
                <select
                  className={selectCls}
                  value={currentU ?? ''}
                  onChange={e => patch({ position_u: e.target.value ? Number(e.target.value) : null })}
                  data-testid={`resolve-u-${index}`}
                >
                  <option value="">— выберите свободный интервал —</option>
                  {freeIntervals.flatMap(iv => {
                    const lastStart = iv.end - uHeight + 1
                    const opts = []
                    for (let s = iv.start; s <= lastStart; s++) {
                      opts.push(
                        <option key={s} value={s}>
                          {uHeight > 1 ? `U${s}–U${s + uHeight - 1}` : `U${s}`}
                          {` (свободно U${iv.start}–U${iv.end})`}
                        </option>,
                      )
                    }
                    return opts
                  })}
                </select>
              </label>
            )}
            {needsCatalog && (
              <label className="block">
                <span className="text-[10px] text-text-muted">Модель из каталога:</span>
                <select
                  className={selectCls}
                  value={(meta.catalog_id as number | undefined) ?? ''}
                  onChange={e => {
                    const id = e.target.value ? Number(e.target.value) : null
                    const row = catalog.find(c => c.id === id)
                    patch({
                      catalog_id: id,
                      catalog_match_confidence: id ? 'exact' : 'none',
                      ...(row?.u_height != null ? { u_height: row.u_height } : {}),
                    })
                  }}
                  data-testid={`resolve-catalog-${index}`}
                >
                  <option value="">— выберите модель —</option>
                  {catalog.map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </label>
            )}
            {needsPort && (
              <label className="block">
                <span className="text-[10px] text-text-muted">Свободный порт:</span>
                <select
                  className={selectCls}
                  value={(meta.port_id as string | undefined) ?? ''}
                  onChange={e => patch({ port_id: e.target.value || null })}
                  data-testid={`resolve-port-${index}`}
                >
                  <option value="">— выберите порт —</option>
                  {candidatePorts.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.connector_type ? ` (${p.connector_type})` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
      </div>
      {blocked
        ? <ShieldAlert className="w-4 h-4 text-status-critical shrink-0 mt-0.5" />
        : item.has_unresolved
          ? <AlertTriangle className="w-4 h-4 text-status-warning shrink-0 mt-0.5" />
          : <CheckCircle2 className="w-4 h-4 text-status-normal shrink-0 mt-0.5" />
      }
    </div>
  )
}
